<!--
  AttachmentDownloadPanel.vue

  Recipient-side download of E2EE attachments. The decryption keys arrive ONLY in
  the URL fragment (`#att=id:key,...`), which never reaches the server. On mount
  we parse the fragment; for each entry we fetch the ciphertext via a one-time
  pre-signed GET, decrypt locally, save the file, then discard the object from
  storage. Filenames (plaintext server metadata) are listed for display.
-->
<script setup lang="ts">
  import {
    useAttachments,
    type FragmentEntry,
  } from '@/apps/secret/composables/useAttachments';
  import { useAuthStore } from '@/shared/stores/authStore';
  import { computed, onMounted, ref } from 'vue';
  import { useI18n } from 'vue-i18n';

  const props = defineProps<{ secretIdentifier: string }>();

  const { t } = useI18n();
  const authStore = useAuthStore();
  const { listAttachments, downloadAttachment, saveBlob, parseAttachmentFragment } =
    useAttachments();

  type Row = {
    entry: FragmentEntry;
    filename: string;
    status: 'idle' | 'downloading' | 'done' | 'error';
    pct: number;
    error: string;
  };

  const rows = ref<Row[]>([]);
  const isGuest = computed(() => authStore.isAuthenticated !== true);
  const hasAttachments = computed(() => rows.value.length > 0);

  onMounted(async () => {
    const entries = parseAttachmentFragment(window.location.hash);
    if (!entries.length) return;
    rows.value = entries.map((entry) => ({
      entry,
      filename: t('web.secrets.attach_encrypted_file'),
      status: 'idle',
      pct: 0,
      error: '',
    }));
    // Best-effort: enrich rows with plaintext filenames from the server.
    try {
      const summaries = await listAttachments(props.secretIdentifier, isGuest.value);
      rows.value.forEach((row) => {
        const match = summaries.find((s) => s.id === row.entry.id);
        if (match?.filename) row.filename = match.filename;
      });
    } catch {
      /* names are optional; the download response also carries the filename */
    }
  });

  async function download(row: Row) {
    if (row.status === 'downloading' || row.status === 'done') return;
    row.status = 'downloading';
    row.pct = 0;
    row.error = '';
    try {
      const { blob, filename } = await downloadAttachment(row.entry, {
        isGuest: isGuest.value,
        onProgress: (loaded, total) => {
          row.pct = total > 0 ? Math.round((loaded / total) * 100) : 0;
        },
      });
      if (filename) row.filename = filename;
      saveBlob(blob, row.filename);
      row.pct = 100;
      row.status = 'done';
    } catch (err) {
      row.status = 'error';
      row.error = err instanceof Error ? err.message : t('web.secrets.attach_download_failed');
    }
  }
</script>

<template>
  <section
    v-if="hasAttachments"
    class="mt-5 overflow-hidden rounded-xl border-2 border-brand-400 bg-brand-50 shadow-sm dark:border-brand-500/60 dark:bg-brand-500/10"
    :aria-label="t('web.secrets.attach_section')">
    <!-- Accent header so the attachment is impossible to miss. -->
    <div
      class="flex items-center gap-2 border-b border-brand-300 bg-brand-500/10 px-4 py-2.5 dark:border-brand-500/30">
      <svg
        class="size-5 shrink-0 text-brand-600 dark:text-brand-400"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
        aria-hidden="true">
        <path
          d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
      </svg>
      <h3 class="text-sm font-semibold text-brand-800 dark:text-brand-200">
        {{ t('web.secrets.attach_section') }}
      </h3>
      <span
        class="ml-1 inline-flex min-w-5 items-center justify-center rounded-full bg-brand-500 px-1.5 py-0.5 text-[11px] font-bold text-white">
        {{ rows.length }}
      </span>
    </div>

    <ul class="flex flex-col gap-2 p-3">
      <li
        v-for="(row, index) in rows"
        :key="row.entry.id"
        class="rounded-lg border border-brand-200 bg-white px-3 py-2.5 dark:border-white/[0.08] dark:bg-night-100"
        :data-testid="`reveal-attachment-${index}`">
        <div class="flex items-center justify-between gap-3">
          <span class="flex min-w-0 items-center gap-2">
            <svg
              class="size-4 shrink-0 text-brand-500 dark:text-brand-400"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true">
              <path
                d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3Z" />
              <circle
                cx="12"
                cy="13"
                r="3" />
            </svg>
            <span class="truncate text-sm font-medium text-day-ink dark:text-night-ink">{{ row.filename }}</span>
          </span>
          <button
            v-if="row.status !== 'done'"
            type="button"
            class="inline-flex shrink-0 items-center gap-1.5 rounded-md bg-brand-500 px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-brand-600 disabled:opacity-60 dark:bg-brand-500 dark:hover:bg-brand-400"
            :disabled="row.status === 'downloading'"
            :data-testid="`reveal-attachment-download-${index}`"
            @click="download(row)">
            <svg
              class="size-3.5"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2.5"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true">
              <path d="M12 3v12m0 0 4-4m-4 4-4-4M5 21h14" />
            </svg>
            {{ row.status === 'downloading' ? t('web.secrets.attach_downloading') : t('web.secrets.attach_download') }}
          </button>
          <span
            v-else
            class="inline-flex shrink-0 items-center gap-1 text-xs font-semibold text-emerald-600 dark:text-emerald-400">
            <svg
              class="size-4"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2.5"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true">
              <path d="M20 6 9 17l-5-5" />
            </svg>
            {{ t('web.secrets.attach_done') }}
          </span>
        </div>
        <div
          v-if="row.status === 'downloading'"
          class="mt-2 h-1.5 overflow-hidden rounded-full bg-brand-100 dark:bg-night-200"
          role="progressbar"
          :aria-valuenow="row.pct"
          aria-valuemin="0"
          aria-valuemax="100">
          <div
            class="h-full rounded-full bg-brand-500 transition-[width] duration-200"
            :style="{ width: `${row.pct}%` }"></div>
        </div>
        <p
          v-if="row.status === 'error'"
          class="mt-1 text-[11px] text-red-600 dark:text-red-400"
          role="alert">
          {{ row.error }}
        </p>
      </li>
    </ul>
    <p class="px-4 pb-3 text-[11px] text-day-ink-muted dark:text-night-ink-muted">
      {{ t('web.secrets.attach_onetime_note') }}
    </p>
  </section>
</template>
